import React, { useEffect, useRef, useState } from 'react';
import {
    Card,
    Grid,
    Typography,
    Select,
    MenuItem,
    Button,
    LinearProgress,
    List,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import FormHelperText from '@mui/material/FormHelperText';
import { useOutletContext } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import { Scrollbars } from 'react-custom-scrollbars-2';

import { useDispatch, useSelector } from 'react-redux';
import { uploadDocs } from '../../redux/actions/DocumentAction';

import DocListItem from './DocListItem';
import { decrypt } from '../../util/Security';

const useStyles = makeStyles((theme) => ({
    progressBar: {
        borderRadius: 4,
        position: 'absolute',
        // width: '100%',
        height: '100%',
        transition: 'width 20s linear',
        marginRight: 16,

        '&::after': {
            content: '"Uploading..."',
            color: 'white',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        },
    },
}));

const DocumentsUpload = () => {
    const [mobileView] = useOutletContext();
    const errorHandler = useErrorHandler();
    const dispatch = useDispatch();
    const supportedFileFormats = '.pdf, .png, .jpg, .jpeg';
    const login = decrypt(localStorage.getItem('login'));
    const [processedFile, setprocessedFile] = useState(null);
    const [uploadButtonText, setUploadButtonText] = useState('Upload Document');
    const [selectedDocument, setSelectedDocument] = useState('');
    const { documentTypes, docUploadProgress } = useSelector((state) => state?.documentState);
    const { candidateUploadedDocs, candidateInfo } = useSelector((state) => state?.candidateState);
    const fileInputRef = useRef();
    const uploadRef = useRef();
    const classes = useStyles();
    const [progress, setProgress] = useState(-1);

    const flexStart = {
        display: 'flex',
        justifyContent: 'stretch',
        alignItems: 'center',
        my: 1,
    };

    const stretchedItems = {
        width: '100%',
        height: '6vh',
        mr: 2,
    };

    useEffect(() => {
        if (docUploadProgress && progress > -1) {
            setProgress(docUploadProgress);
            setTimeout(() => {
                setProgress(-1);
            }, 100);
        } else if (docUploadProgress?.error) {
            setProgress(-1);
        }
    }, [docUploadProgress]);
    useEffect(() => {
        if (selectedDocument) {
            if (selectedDocument === 'Degree Certificate' || selectedDocument === 'Others')
                setUploadButtonText('Upload Document');
            else {
                const present = candidateUploadedDocs.find((doc) => {
                    const fileName = doc.file_name.split('.')[0];
                    return fileName === selectedDocument;
                });
                present
                    ? setUploadButtonText('Replace Document')
                    : setUploadButtonText('Upload Document');
            }
        }
    }, [selectedDocument]);
    const largeFileChecker = (file) => {
        const selectedFileSize = file?.size / 1000000; //byte to MB

        if (selectedFileSize < candidateInfo?.max_size_to_doc_upload) return false;

        let notify = {
            msg: `Selected file is larger than ${candidateInfo?.max_size_to_doc_upload} MB`,
            theme: 'error',
        };
        dispatch({ type: 'NOTIFICATION', payload: notify });
        return true;
    };
    const processFile = async (e) => {
        try {
            const file = e?.target?.files[0];
            if (!file || largeFileChecker(file)) return;
            const fileType = file?.type.split('/')[1];
            let base64Url = file && (await getBaseUrl(file));
            base64Url = base64Url?.split(',')[1];

            const fileData = {
                candidate_id: login.candidateId,
                attchment_data: base64Url,
                attchment_name: `.${fileType}`,
            };
            setprocessedFile(fileData);
        } catch (error) {
            errorHandler(error);
        }
    };
    const handleDocUpload = () => {
        try {
            if (!processedFile || !selectedDocument) return;
            const fileToUpload = {
                ...processedFile,
                attchment_name: `${selectedDocument}${processedFile.attchment_name}`,
            };
            dispatch(uploadDocs(fileToUpload));
            setProgress(0);
            setprocessedFile(null);
            setSelectedDocument('');
        } catch (error) {
            errorHandler(error);
        }
    };

    const getBaseUrl = async (file) => {
        try {
            let reader = new FileReader();
            return new Promise((resolve) => {
                reader.readAsDataURL(file);
                reader.onload = (event) => resolve(event.target.result);
            });
        } catch (error) {
            errorHandler(error);
        }
    };

    const handleChange = (e) => {
        const selectedDoc = e.target.value;
        setSelectedDocument(selectedDoc);
    };

    return (
        <Card sx={{ mx: mobileView ? 4 : 8, my: 4 }}>
            <Grid container p={3}>
                <Grid item xs={12}>
                    <Typography variant='body1' textAlign='start' mb={2} fontWeight={600}>
                        Please upload required documents below:
                    </Typography>
                </Grid>
                <Grid item container sm={12} xs={12} display='flex' alignItems='flex-start'>
                    <Grid item container sm={6} xs={12} sx={{ ...flexStart }}>
                        <Select
                            sx={stretchedItems}
                            value={selectedDocument}
                            onChange={handleChange}>
                            {documentTypes &&
                                documentTypes.map((docName) => (
                                    <MenuItem key={docName} value={docName}>
                                        <Typography variant='body2'>{docName}</Typography>
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText error>
                            {` (File size allowed: ${candidateInfo?.max_size_to_doc_upload}MB max, Accepted file formats: ${supportedFileFormats} )`}
                        </FormHelperText>
                    </Grid>
                    <Grid item sm={3} xs={12} sx={{ ...flexStart }}>
                        <input
                            style={{ display: 'none' }}
                            type='file'
                            ref={fileInputRef}
                            accept={supportedFileFormats}
                            onClick={() => setprocessedFile(null)}
                            onChange={(e) => processFile(e)}
                        />
                        <Button
                            sx={stretchedItems}
                            variant='contained'
                            onClick={() => {
                                fileInputRef.current.click();
                            }}
                            disabled={!selectedDocument}>
                            {processedFile ? 'Selected' : 'Choose File'}
                        </Button>
                    </Grid>
                    <Grid item sm={3} xs={12} sx={{ ...flexStart, position: 'relative' }}>
                        <Button
                            ref={uploadRef}
                            sx={{ ...stretchedItems }}
                            size='small'
                            variant='outlined'
                            onClick={handleDocUpload}
                            disabled={!processedFile}>
                            {uploadButtonText}
                        </Button>
                        {progress > -1 ? (
                            <LinearProgress
                                // variant='determinate'
                                // value={progress}
                                color='success'
                                className={classes.progressBar}
                                sx={{ width: uploadRef.current?.clientWidth + 2 }}
                            />
                        ) : null}
                        {/* )} */}
                    </Grid>
                </Grid>

                <Grid item container sm={8} xs={12}>
                    <Grid item xs={12} sx={{ ...flexStart }}>
                        <Typography variant='body1' fontWeight={600}>
                            Uploaded Documents :
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                            <List style={{ paddingRight: 10 }}>
                                {candidateUploadedDocs.length > 0 ? (
                                    candidateUploadedDocs.map((doc) => {
                                        return <DocListItem key={doc?.id} props={doc} />;
                                    })
                                ) : (
                                    // <Typography variant='body2'>No Documents Uploaded</Typography>
                                    <FormHelperText>No Documents Uploaded</FormHelperText>
                                )}
                            </List>
                    </Grid>
                </Grid>
            </Grid>
        </Card>
    );
};

export default DocumentsUpload;
